import { logger } from '@/core/logger';
import { normalizeLocaleNamespace } from '@/core/services/localeNamespace';
import {
  CONTENTFUL_LOCALES_AUTH_KEY_EMIRATES_ID_OTP_RESEND_SECONDS,
  DEFAULT_EMIRATES_ID_OTP_RESEND_SECONDS,
  parseEmiratesIdOtpResendSeconds,
} from '@/modules/auth/utils/emiratesIdOtpResendSeconds';

type Locale = 'en' | 'ar';

const AUTH_NAMESPACE = 'auth';

let configuredSeconds: number = DEFAULT_EMIRATES_ID_OTP_RESEND_SECONDS;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore subscriber errors
    }
  });
}

function resolveLocalesNamespace(
  fields: Record<string, unknown>,
  includeById: Record<string, { fields?: Record<string, unknown> }>,
): string | undefined {
  const explicitNs =
    typeof fields.namespace === 'string' ? fields.namespace.trim() : undefined;
  if (explicitNs) {
    return normalizeLocaleNamespace(explicitNs);
  }

  const maybeType = fields.type as
    | { fields?: { value?: unknown }; sys?: { id?: string } }
    | undefined;

  if (maybeType?.fields?.value && typeof maybeType.fields.value === 'string') {
    return normalizeLocaleNamespace(maybeType.fields.value);
  }

  const typeId = maybeType?.sys?.id;
  if (typeId && includeById[typeId]?.fields?.value) {
    const value = includeById[typeId]?.fields?.value;
    if (typeof value === 'string') {
      return normalizeLocaleNamespace(value);
    }
  }

  return undefined;
}

function resolveLocalesEntryKey(fields: Record<string, unknown>): string {
  if (typeof fields.key === 'string') {
    return fields.key.trim();
  }
  if (typeof fields.title === 'string') {
    return fields.title.trim();
  }
  return '';
}

function parseEmiratesIdOtpResendFromLocalesRaw(raw: unknown): number {
  const items: unknown[] = Array.isArray((raw as { items?: unknown[] })?.items)
    ? ((raw as { items: unknown[] }).items ?? [])
    : [];
  const includes: unknown[] = Array.isArray(
    (raw as { includes?: { Entry?: unknown[] } })?.includes?.Entry,
  )
    ? ((raw as { includes: { Entry: unknown[] } }).includes.Entry ?? [])
    : [];

  const includeById: Record<string, { fields?: Record<string, unknown> }> = {};
  for (const inc of includes) {
    const id = (inc as { sys?: { id?: string } })?.sys?.id;
    if (id) {
      includeById[id] = inc as { fields?: Record<string, unknown> };
    }
  }

  for (const item of items) {
    const fields = (item as { fields?: Record<string, unknown> })?.fields ?? {};
    const keyField = resolveLocalesEntryKey(fields);

    if (keyField !== CONTENTFUL_LOCALES_AUTH_KEY_EMIRATES_ID_OTP_RESEND_SECONDS) {
      continue;
    }

    const namespace = resolveLocalesNamespace(fields, includeById);
    if (namespace !== AUTH_NAMESPACE) {
      continue;
    }

    return parseEmiratesIdOtpResendSeconds(fields.value);
  }

  return DEFAULT_EMIRATES_ID_OTP_RESEND_SECONDS;
}

export function getEmiratesIdOtpResendSeconds(): number {
  return configuredSeconds;
}

export function subscribeEmiratesIdOtpResendConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read `auth.emiratesIdOtpResendSeconds` from the standard Contentful `locales` fetch.
 */
export function applyEmiratesIdOtpResendFromLocalesRaw(
  raw: unknown,
  locale?: Locale,
): number {
  const seconds = parseEmiratesIdOtpResendFromLocalesRaw(raw);
  configuredSeconds = seconds;

  logger.info('emiratesIdOtpResendConfigService', 'Applied from locales payload', {
    locale,
    emiratesIdOtpResendSeconds: seconds,
  });

  notifyListeners();
  return seconds;
}

export function restoreEmiratesIdOtpResendFromCache(
  seconds: number | undefined,
): void {
  configuredSeconds =
    seconds != null && Number.isFinite(seconds)
      ? seconds
      : DEFAULT_EMIRATES_ID_OTP_RESEND_SECONDS;
  notifyListeners();
}
